import { useState, useCallback, useEffect } from 'react';
import { useApiKeyPopup } from '@pineseed/gui/hook/useApiKeyPopup';
import { Key, Copy, Check, X } from 'lucide-react';

export interface ApiKeyGeneratorProps {
  baseUrl: string;
  workspaceId: string;
  authConfig: {
    casdoor: {
      serverUrl: string;
      clientId: string;
      appName: string;
      organizationName: string;
      redirectPath: string;
      signinPath?: string;
      ui: {
        mode: 'redirect' | 'embedded' | 'popup' | 'silent';
        theme?: 'light' | 'dark' | 'auto';
        customization?: Record<string, unknown>;
      };
      sso?: {
        enabled: boolean;
        providers?: string[];
      };
      providerConfig?: Record<string, unknown>;
    };
  };
}

export function ApiKeyGenerator({ baseUrl, workspaceId, authConfig }: ApiKeyGeneratorProps) {
  const { generateApiKeyWithPopup, clearKey, loading, error, generatedKey } = useApiKeyPopup(workspaceId, baseUrl, authConfig);
  const [ttl, setTtl] = useState<string>('3600');
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  const handleGenerate = useCallback(async () => {
    const ttlNum = parseInt(ttl, 10);
    if (isNaN(ttlNum) || ttlNum <= 0) {
      return;
    }
    try {
      const result = await generateApiKeyWithPopup(ttlNum);
      setRemainingSeconds(result.expiresIn);
    } catch (err) {
      // Error is handled by the hook
    }
  }, [ttl, generateApiKeyWithPopup]);

  const handleCopy = useCallback(async () => {
    if (!generatedKey) return;
    
    try {
      await navigator.clipboard.writeText(generatedKey.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [generatedKey]);

  const handleClose = useCallback(() => {
    clearKey();
    setIsOpen(false);
    setCopied(false);
    setRemainingSeconds(null);
  }, [clearKey]);

  // Countdown timer
  useEffect(() => {
    if (remainingSeconds === null || remainingSeconds <= 0) return;

    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev === null || prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingSeconds]);

  const isValidTtl = !isNaN(parseInt(ttl, 10)) && parseInt(ttl, 10) > 0;

  return (
    <div className="relative">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title="Generate API key"
      >
        <Key className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          API Keys
        </span>
        <svg className={`w-3 h-3 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-[420px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Generate API Key
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* TTL Input */}
            {!generatedKey && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Time to Live (seconds)
                </label>
                <input
                  type="number"
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  min="1"
                  placeholder="3600"
                  className={`w-full px-3 py-2 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    isValidTtl 
                      ? 'border-gray-300 dark:border-gray-600' 
                      : 'border-red-300 dark:border-red-600'
                  }`}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {isValidTtl && parseInt(ttl, 10) > 0 && (
                    <>Expires in {Math.floor(parseInt(ttl, 10) / 3600)}h {Math.floor((parseInt(ttl, 10) % 3600) / 60)}m</>
                  )}
                  {!isValidTtl && <span className="text-red-500">Must be a positive number</span>}
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {/* Generated Key Display */}
            {generatedKey && (
              <div className="space-y-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <div className="text-xs font-medium text-green-700 dark:text-green-300 mb-2">
                    ✓ API Key Generated
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs font-mono text-gray-900 dark:text-gray-100 break-all">
                      {generatedKey.apiKey}
                    </code>
                    <button
                      onClick={handleCopy}
                      className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Expires in {remainingSeconds !== null && remainingSeconds > 0
                      ? `${Math.floor(remainingSeconds / 3600)}h ${Math.floor((remainingSeconds % 3600) / 60)}m ${remainingSeconds % 60}s`
                      : 'Expired'}
                  </div>
                </div>
                <button
                  onClick={clearKey}
                  className="w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                >
                  Generate Another
                </button>
              </div>
            )}

            {/* Generate Button */}
            {!generatedKey && (
              <button
                onClick={handleGenerate}
                disabled={loading || !isValidTtl}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors"
              >
                {loading ? 'Generating...' : 'Generate API Key'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

